"use client";

/**
 * Generic sessionStorage cache for API responses.
 *
 * Pattern: stale-while-revalidate.
 *   1. On mount, call getCached(key) — if data exists, render it immediately.
 *   2. Fire network request in background.
 *   3. On success, setCached(key, data) updates cache + state.
 *   4. On failure, fall back to existing cache (no blank flash).
 *
 * Uses sessionStorage (cleared on tab close) so stale data never persists
 * across sessions. Keyed by user UID to prevent cross-user leakage.
 */

const CACHE_PREFIX = "aiia_cache:";
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 minutes

function safeSessionStorage() {
  try {
    if (typeof window === "undefined") return null;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function getCached(key) {
  const store = safeSessionStorage();
  if (!store) return null;
  try {
    const raw = store.getItem(CACHE_PREFIX + key);
    if (!raw) return null;
    const { data, ts, ttl } = JSON.parse(raw);
    if (Date.now() - ts > (ttl || DEFAULT_TTL_MS)) {
      store.removeItem(CACHE_PREFIX + key);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

export function setCached(key, data, ttlMs = DEFAULT_TTL_MS) {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.setItem(
      CACHE_PREFIX + key,
      JSON.stringify({ data, ts: Date.now(), ttl: ttlMs })
    );
  } catch {
    // Quota exceeded — evict oldest entries and try once more
    try {
      evictOldest(store);
      store.setItem(
        CACHE_PREFIX + key,
        JSON.stringify({ data, ts: Date.now(), ttl: ttlMs })
      );
    } catch {
      /* give up — cache is a best-effort optimization */
    }
  }
}

/** Invalidate specific cache entry (after a mutation). */
export function invalidateCache(key) {
  const store = safeSessionStorage();
  if (!store) return;
  try { store.removeItem(CACHE_PREFIX + key); } catch {}
}

/** Clear ALL aiia cache entries (e.g. on logout). */
export function clearAllCache() {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    const toRemove = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith(CACHE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => store.removeItem(k));
  } catch {}
}

/** Internal: remove oldest 25% of entries when quota is full. */
function evictOldest(store) {
  const entries = [];
  for (let i = 0; i < store.length; i++) {
    const k = store.key(i);
    if (!k || !k.startsWith(CACHE_PREFIX)) continue;
    try {
      const parsed = JSON.parse(store.getItem(k));
      entries.push({ k, ts: parsed.ts || 0 });
    } catch {
      store.removeItem(k); // corrupt entry
    }
  }
  entries.sort((a, b) => a.ts - b.ts);
  const toEvict = Math.max(1, Math.floor(entries.length / 4));
  for (let i = 0; i < toEvict; i++) store.removeItem(entries[i].k);
}
